import {
  calcOptionsOf,
  calculateConsumption,
  calculateContractFinancials,
  calculateProjectFinancials,
  hiddenOnCreate,
  rollupPlan,
  seedCategoryFactors,
  targetFactorOf,
} from '@timelog/pm-shared';
import type {
  BudgetCalcOptions,
  CategoryId,
  ContractConsumption,
  ContractFinancials,
  ContractInput,
  ContractResponse,
  ContractType,
  MoveCategoryRequest,
  ProjectFinancials,
  ProjectResponse,
} from '@timelog/pm-shared';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import type { Dispatch, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { nextContractName } from './contractFactory';
import { contractReducer, createInitialContractState } from './contractStore';
import type { ContractEditorAction, ContractEditorState } from './contractStore';
import { useContractAutosave } from './useContractAutosave';
import type { FlushResult } from './useContractAutosave';

import { useBudgetModel } from '@/analytics';
import type { BudgetModelValue } from '@/analytics';
import { captureEvent } from '@/analytics/posthogClient';
import {
  useContracts,
  useCreateContract,
  useDeleteContract,
  useDuplicateContract,
  useMoveCategory,
} from '@/api';

/**
 * Owns the ACTIVE contract's editor state and every derived number the
 * Financials page shows. One reducer + one autosave loop (ADR 0010 applied to
 * the contract aggregate; see contractStore.ts): siblings live in the query
 * cache and `effectiveContracts` overlays local state onto the active row so
 * the Contracts panel, the aside and the project rollup track every keystroke
 * commit without waiting for a save.
 */

export type FinancialsActions = {
  /** Flush-first switch; blocked while a conflict is unresolved or a save fails. */
  selectContract: (id: string) => Promise<void>;
  createContract: (type?: ContractType) => Promise<void>;
  /** Duplicates from the LOCAL overlay when the source is the active contract. */
  duplicateContract: (id: string) => Promise<void>;
  /** Throws ApiError (409 carries the BR-7 leaf count) — the dialog handles it. */
  deleteContract: (id: string, reassignTo?: string) => Promise<void>;
  /** BR-10 both directions, transactional (ADR 0011). Flushes first. */
  moveCategory: (
    categoryId: Extract<CategoryId, 'exp' | 'sub' | 'mat'>,
    targetContractId: string,
    direction: 'out' | 'back',
  ) => Promise<void>;
  flushSave: () => Promise<FlushResult>;
};

/**
 * What the right panel shows (3 Nov, Mette): the active contract, or the
 * project overview across every contract — as Planning's panel shows a task
 * or the project. A view preference, deliberately NOT in the reducer: every
 * inbound sync (`RESET_FROM_SERVER` / `ADOPT_SERVER`) would snap it back, the
 * reason `planning/PlanViewContext` keeps its List/Timeline choice out too.
 */
export type FinancialsAsideView = 'contract' | 'project';

export type FinancialsContextValue = {
  state: ContractEditorState;
  dispatch: Dispatch<ContractEditorAction>;
  project: ProjectResponse;
  blendedCostRate: number;
  /** The resolved budget-model config (ADR 0027) — read via accessors only. */
  budgetModel: BudgetModelValue;
  /** CALC-19/20 options derived from the config, passed to every shared calc. */
  calcOptions: BudgetCalcOptions;
  /** Server list with the active row overlaid by local editor state. */
  effectiveContracts: ContractResponse[];
  activeFinancials: ContractFinancials;
  projectFinancials: ProjectFinancials;
  consumption: ContractConsumption[];
  actions: FinancialsActions;
  asideView: FinancialsAsideView;
  setAsideView: (view: FinancialsAsideView) => void;
};

const FinancialsContext = createContext<FinancialsContextValue | undefined>(undefined);

export type FinancialsProviderProps = {
  project: ProjectResponse;
  /** Non-empty — the page renders its own empty state before mounting this. */
  contracts: ContractResponse[];
  children: ReactNode;
};

const CONTRACT_PARAM = 'contract';

export const FinancialsProvider = ({ project, contracts, children }: FinancialsProviderProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation(['pmFinancials', 'pmCommon']);

  // ── Active contract resolution: ?contract= → project default → first row.
  const requestedId = searchParams.get(CONTRACT_PARAM);
  const activeRow = useMemo(() => {
    const byId = (id: string | null | undefined) =>
      contracts.find((contract) => contract.id === id);
    return byId(requestedId) ?? byId(project.document.defaultContractId) ?? contracts[0]!;
  }, [contracts, requestedId, project.document.defaultContractId]);

  const [state, dispatch] = useReducer(contractReducer, activeRow, createInitialContractState);

  // Re-key the editor when a DIFFERENT contract becomes active. Refetches of
  // the same contract never overwrite local edits implicitly.
  useEffect(() => {
    if (state.contractId !== activeRow.id) {
      dispatch({ type: 'RESET_FROM_SERVER', contract: activeRow });
    }
  }, [activeRow, state.contractId]);

  const { flush } = useContractAutosave(project.id, state, dispatch);

  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  });

  // The choice is kept with the contract it was made on, so any contract
  // becoming active — picked, created, duplicated, or the next one after a
  // delete — opens the panel on it, without an effect to reset it.
  const [asideChoice, setAsideChoice] = useState<{
    contractId: string;
    view: FinancialsAsideView;
  } | null>(null);
  const asideView: FinancialsAsideView =
    asideChoice?.contractId === state.contractId ? asideChoice.view : 'contract';
  const setAsideView = useCallback(
    (view: FinancialsAsideView) =>
      setAsideChoice({ contractId: stateRef.current.contractId, view }),
    [],
  );

  // ── Mutations (list-cache invalidation lives in the hooks).
  const createMutation = useCreateContract(project.id);
  const duplicateMutation = useDuplicateContract(project.id);
  const deleteMutation = useDeleteContract(project.id);
  const moveMutation = useMoveCategory(project.id);
  const contractsQuery = useContracts(project.id);

  const blendedCostRate = project.document.blendedCostRate;
  const budgetModel = useBudgetModel();
  const calcOptions = useMemo(() => calcOptionsOf(budgetModel.config), [budgetModel.config]);

  // ── Derived numbers, all from the shared calcs over the local overlay.
  const effectiveContracts = useMemo<ContractResponse[]>(
    () =>
      contracts.map((contract) =>
        contract.id === state.contractId
          ? {
              ...contract,
              name: state.name,
              type: state.type,
              version: state.serverVersion,
              document: state.document,
            }
          : contract,
      ),
    [contracts, state.contractId, state.name, state.type, state.serverVersion, state.document],
  );

  const contractInputs = useMemo<ContractInput[]>(
    () =>
      effectiveContracts.map((contract) => ({
        id: contract.id,
        name: contract.name,
        type: contract.type,
        document: contract.document,
      })),
    [effectiveContracts],
  );

  const activeFinancials = useMemo(
    () => calculateContractFinancials(state.document, blendedCostRate, calcOptions),
    [state.document, blendedCostRate, calcOptions],
  );

  const projectFinancials = useMemo(
    () => calculateProjectFinancials(contractInputs, blendedCostRate, calcOptions),
    [contractInputs, blendedCostRate, calcOptions],
  );

  // The Financials page reads the plan (never edits it): consumption pairs the
  // server plan document with the LIVE contract budgets (CALC-15).
  const consumption = useMemo(() => {
    const rollup = rollupPlan(project.document, contractInputs);
    return calculateConsumption(contractInputs, project.document, rollup, calcOptions);
  }, [project.document, contractInputs, calcOptions]);

  // ── Actions.
  const setActiveParam = useCallback(
    (id: string) => {
      setSearchParams(
        (params) => {
          const next = new URLSearchParams(params);
          next.set(CONTRACT_PARAM, id);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const selectContract = useCallback(
    async (id: string) => {
      // Picking a contract shows it, also the one already active — from the
      // project overview, that is the way back.
      setAsideView('contract');
      if (id === stateRef.current.contractId) return;
      const result = await flush();
      // A conflict keeps the dialog up; an error keeps the user here so the
      // retry loop can finish — switching would abandon the unsaved edits.
      if (result.status !== 'clean') return;
      setActiveParam(id);
    },
    [flush, setActiveParam, setAsideView],
  );

  const createContract = useCallback(
    async (type: ContractType = 'fixed') => {
      const flushResult = await flush();
      if (flushResult.status !== 'clean') return;
      const created = await createMutation.mutateAsync({
        name: nextContractName(
          t,
          contracts.map((contract) => contract.name),
          project.document.name,
        ),
        type,
        // BR-27 / CALC-25 — category presence, the target and the per-section
        // factors all seed from the config. Since schema 2 the factors ride the
        // flag payload rather than `CATEGORY_DEFS`, so they travel with the rest.
        seed: {
          hiddenCategories: hiddenOnCreate(budgetModel.config),
          targetFactor: targetFactorOf(budgetModel.config),
          categoryFactors: seedCategoryFactors(budgetModel.config),
        },
      });
      captureEvent('contract_created', { type });
      setActiveParam(created.id);
    },
    [
      contracts,
      createMutation,
      flush,
      setActiveParam,
      budgetModel.config,
      project.document.name,
      t,
    ],
  );

  const duplicateContract = useCallback(
    async (id: string) => {
      const flushResult = await flush();
      if (flushResult.status !== 'clean') return;
      const source = effectiveContracts.find((contract) => contract.id === id);
      if (!source) return;
      const copy = await duplicateMutation.mutateAsync(source);
      setActiveParam(copy.id);
    },
    [duplicateMutation, effectiveContracts, flush, setActiveParam],
  );

  const deleteContract = useCallback(
    async (id: string, reassignTo?: string) => {
      await deleteMutation.mutateAsync({ contractId: id, ...(reassignTo ? { reassignTo } : {}) });
      if (id === stateRef.current.contractId) {
        const remaining = contracts.filter((contract) => contract.id !== id);
        const next = reassignTo ?? remaining[0]?.id;
        if (next) setActiveParam(next);
      }
    },
    [contracts, deleteMutation, setActiveParam],
  );

  const moveCategory = useCallback(
    async (
      categoryId: Extract<CategoryId, 'exp' | 'sub' | 'mat'>,
      targetContractId: string,
      direction: 'out' | 'back',
    ) => {
      const flushResult = await flush();
      if (flushResult.status !== 'clean') return;
      const target =
        contractsQuery.data?.contracts.find((contract) => contract.id === targetContractId) ??
        contracts.find((contract) => contract.id === targetContractId);
      if (!target) return;

      const request: MoveCategoryRequest = {
        categoryId,
        targetContractId,
        direction,
        version: flushResult.version,
        targetVersion: target.version,
      };
      const response = await moveMutation.mutateAsync({
        contractId: stateRef.current.contractId,
        request,
      });
      // The endpoint bumped our row: adopt the returned source wholesale (the
      // editor was clean — flush-first — so nothing local is lost).
      dispatch({ type: 'ADOPT_SERVER', contract: response.source });
    },
    [contracts, contractsQuery.data, flush, moveMutation],
  );

  const actions = useMemo<FinancialsActions>(
    () => ({
      selectContract,
      createContract,
      duplicateContract,
      deleteContract,
      moveCategory,
      flushSave: flush,
    }),
    [selectContract, createContract, duplicateContract, deleteContract, moveCategory, flush],
  );

  const value = useMemo<FinancialsContextValue>(
    () => ({
      state,
      dispatch,
      project,
      blendedCostRate,
      budgetModel,
      calcOptions,
      effectiveContracts,
      activeFinancials,
      projectFinancials,
      consumption,
      actions,
      asideView,
      setAsideView,
    }),
    [
      state,
      project,
      blendedCostRate,
      budgetModel,
      calcOptions,
      effectiveContracts,
      activeFinancials,
      projectFinancials,
      consumption,
      actions,
      asideView,
      setAsideView,
    ],
  );

  return <FinancialsContext.Provider value={value}>{children}</FinancialsContext.Provider>;
};

export const useFinancials = (): FinancialsContextValue => {
  const context = useContext(FinancialsContext);
  if (!context) {
    throw new Error('useFinancials must be used inside a FinancialsProvider');
  }
  return context;
};
